import { User } from '../types/alarm';
import { auth } from '../firebase/config';
import { signOut, deleteUser } from 'firebase/auth';

const USER_STORAGE_KEY = 'alarm-app-user-custom-data';

export const saveUserCustomData = (user: User): void => {
  try {
    // 클래스 정보를 포함한 커스텀 데이터 저장
    const customData = {
      id: user.id,
      email: user.email,
      name: user.name,
      picture: user.picture,
      userType: user.userType,
      currentClassId: user.currentClassId
    };
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(customData));
  } catch (error) {
    console.error('사용자 커스텀 정보 저장 중 오류:', error);
  }
};

export const loadUserCustomData = (): User | null => {
  try {
    const stored = localStorage.getItem(USER_STORAGE_KEY);
    if (!stored) return null;
    
    const parsedData = JSON.parse(stored);
    
    // 클래스 데이터의 createdAt을 Date 객체로 변환
    if (parsedData.classes) {
      parsedData.classes = parsedData.classes.map((cls: any) => ({
        ...cls,
        createdAt: new Date(cls.createdAt)
      }));
    }
    
    return parsedData;
  } catch (error) {
    console.error('사용자 커스텀 정보 로드 중 오류:', error);
    return null;
  }
};

export const clearUserFromStorage = (): void => {
  try {
    localStorage.removeItem(USER_STORAGE_KEY);
  } catch (error) {
    console.error('사용자 정보 삭제 중 오류:', error);
  }
};

// Firebase Auth 계정 삭제 함수
export const deleteFirebaseUser = async (): Promise<void> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    await signOut(auth).catch(() => undefined);
    throw new Error('로그인된 사용자가 없습니다.');
  }

  try {
    await deleteUser(currentUser);
    console.log('Firebase 사용자 계정이 삭제되었습니다.');
  } catch (error: any) {
    console.error('계정 삭제 실패:', error);

    if (error?.code === 'auth/requires-recent-login') {
      // 최근 로그인 필요: 계정/DB는 아직 삭제되지 않음. 재로그인 후 다시 시도하도록 안내
      throw new Error('최근 로그인한 후 다시 시도해주세요. (아직 계정과 데이터는 삭제되지 않았습니다.)');
    }

    // 기타 오류는 상위로 전달
    throw new Error(error?.message || '계정 삭제 중 오류가 발생했습니다.');
  }
};

// 특정 이메일의 Firebase Auth 계정 삭제 (관리자 권한 필요)
export const deleteUserByEmail = async (email: string): Promise<void> => {
  try {
    // Firebase Functions를 통해 서버 사이드에서 계정 삭제
    // 현재는 클라이언트 사이드에서 직접 삭제 시도
    const currentUser = auth.currentUser;
    if (currentUser && currentUser.email === email) {
      await deleteUser(currentUser);
      console.log(`이메일 ${email}의 계정이 삭제되었습니다.`);
    } else {
      console.log('해당 이메일의 계정이 현재 로그인되어 있지 않습니다.');
      // 로그아웃만 진행
      await signOut(auth);
    }
  } catch (error) {
    console.error('계정 삭제 중 오류:', error);
    // 로그아웃 시도
    try {
      await signOut(auth);
      console.log('Firebase 로그아웃이 완료되었습니다.');
    } catch (signOutError) {
      console.error('Firebase 로그아웃 중 오류:', signOutError);
    }
  }
};

// 모든 사용자 관련 데이터 초기화
export const clearAllUserStorage = async (): Promise<void> => {
  try {
    // Firebase Auth 로그아웃
    await deleteFirebaseUser();
    
    // 사용자 커스텀 데이터 삭제
    localStorage.removeItem(USER_STORAGE_KEY);
    
    // Firebase Auth 관련 데이터도 삭제 (가능한 경우)
    if (typeof window !== 'undefined' && window.localStorage) {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.includes('firebase') || key.includes('auth') || key.includes('user')) {
          localStorage.removeItem(key);
        }
      });
    }
    
    // IndexedDB도 정리 (Firebase가 사용할 수 있음)
    if (typeof window !== 'undefined' && window.indexedDB) {
      try {
        const databases = await window.indexedDB.databases();
        databases.forEach(db => {
          if (db.name && db.name.includes('firebase')) {
            window.indexedDB.deleteDatabase(db.name);
          }
        });
      } catch (error) {
        console.log('IndexedDB 정리 중 오류:', error);
      }
    }
    
    console.log('모든 사용자 관련 데이터가 초기화되었습니다.');
  } catch (error) {
    console.error('사용자 데이터 초기화 중 오류:', error);
  }
}; 